import React, { useState } from "react";
import ListCard from "../../../components/UI/ListCard";
import Badge from "../../../components/common/Badge";
import Modal from "../../../components/common/Modal";
import ProgressBar from "../../../components/common/ProgressBar";
import Button from "../../../components/common/Button";
import WhereUsedContent from "./WhereUsedContent";

const statusVariants = {
  ACTIVE: "success",
  INACTIVE: "danger",
};

const unitsInBytes = {
  B: 1,
  KB: 1024,
  MB: 1024 * 1024,
  GB: 1024 * 1024 * 1024,
};

const parseSize = (sizeStr) => {
  // Match number and unit (e.g., "0.32 MB")
  const match = sizeStr.match(/([\d.]+)\s*(B|KB|MB|GB)/i);
  if (match) {
    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();
    const bytes = value * unitsInBytes[unit];
    return { value, unit, bytes };
  }
  return null;
};

const UsageCard = ({
  title,
  usage = {},
  limits = {},
  enabled = true,
  serviceKey,
}) => {
  const [modalContent, setModalContent] = useState(null);

  const isEnabled = enabled !== false; // Treat undefined or true as enabled

  const handleOpenModal = (type, data, title) => {
    setModalContent({ type, data, title });
  };

  const handleOpenWhereUsedModal = () => {
    if (serviceKey === "media" || serviceKey === "twilio") {
      setModalContent({
        type: "whereUsed",
        data: { serviceKey, isEnabled, title },
        title: `Where is ${title} used?`,
      });
    }
  };

  const handleCloseModal = () => {
    setModalContent(null);
  };

  const renderUsageItem = (key, value) => {
    const limitValue = limits[key];
    let percentage = null;
    let progressBar = null;

    let displayValue = value;
    let displayLimit = limitValue;

    // Handle values that are strings with units
    if (typeof value === "string" && typeof limitValue === "string") {
      const parsedValue = parseSize(value);
      const parsedLimit = parseSize(limitValue);
      if (parsedValue && parsedLimit) {
        percentage = Math.min(
          Math.round((parsedValue.bytes / parsedLimit.bytes) * 100),
          100
        );
        displayValue = `${parsedValue.value} ${parsedValue.unit}`;
        displayLimit = `${parsedLimit.value} ${parsedLimit.unit}`;
        progressBar = (
          <ProgressBar
            percentage={percentage}
            label={`${displayValue} / ${displayLimit}`}
            color="primary"
          />
        );
      }
    } else if (typeof value === "number" && typeof limitValue === "number") {
      percentage = Math.min(Math.round((value / limitValue) * 100), 100);
      progressBar = (
        <ProgressBar
          percentage={percentage}
          label={`${value} / ${limitValue}`}
          color="primary"
        />
      );
    }

    return (
      <div key={key} className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">
            {key.replace(/([A-Z])/g, " $1")}
          </span>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">
              {typeof value === "object" && value !== null ? "" : displayValue}
              {limitValue && typeof value !== "object"
                ? ` / ${displayLimit}`
                : ""}
            </span>
            {(Array.isArray(value) ||
              (typeof value === "object" && value !== null)) && (
              <Button
                onClick={() =>
                  handleOpenModal(
                    "details",
                    value,
                    key.replace(/([A-Z])/g, " $1")
                  )
                }
                size="sm"
              >
                View Details
              </Button>
            )}
          </div>
        </div>
        {progressBar}
      </div>
    );
  };

  const renderUsage = () =>
    Object.entries(usage).map(([key, value]) => renderUsageItem(key, value));

  return (
    <ListCard className={`relative ${!isEnabled ? "bg-gray-200" : ""}`}>
      <div className="flex flex-col lg:flex-row justify-between items-center mb-4">
        <div className="mb-2 lg:mb-0 w-full lg:w-auto">
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-sm text-neutral-dark">
            Status:{" "}
            <Badge
              variant={
                isEnabled ? statusVariants.ACTIVE : statusVariants.INACTIVE
              }
            >
              {isEnabled ? "ACTIVE" : "INACTIVE"}
            </Badge>
          </p>
        </div>
        {(serviceKey === "media" || serviceKey === "twilio") && (
          <Button variant="primary-text" onClick={handleOpenWhereUsedModal}>
            Where Used?
          </Button>
        )}
      </div>

      <div className="border-t pt-2">{renderUsage()}</div>

      {modalContent && (
        <Modal
          isOpen={!!modalContent}
          closeModal={handleCloseModal}
          title={modalContent.title}
          isFullScreen={modalContent.type === "details"}
        >
          {modalContent.type === "details" && (
            <div>
              {Array.isArray(modalContent.data) ? (
                modalContent.data.length > 0 ? (
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        {Object.keys(modalContent.data[0]).map((key) => (
                          <th
                            key={key}
                            className="py-2 px-4 border-b border-gray-200 bg-gray-100 text-left text-sm font-medium text-gray-700 uppercase tracking-wider"
                          >
                            {key.replace(/([A-Z])/g, " $1")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {modalContent.data.map((item, index) => (
                        <tr key={index}>
                          {Object.values(item).map((value, idx) => (
                            <td
                              key={idx}
                              className="py-2 px-4 border-b border-gray-200"
                            >
                              {typeof value === "object" && value !== null
                                ? JSON.stringify(value)
                                : value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No data available.</p>
                )
              ) : typeof modalContent.data === "object" ? (
                <pre className="bg-gray-100 p-4 rounded overflow-auto">
                  {JSON.stringify(modalContent.data, null, 2)}
                </pre>
              ) : (
                <p>{modalContent.data}</p>
              )}
            </div>
          )}
          {modalContent.type === "whereUsed" && (
            <WhereUsedContent
              serviceKey={modalContent.data.serviceKey}
              isEnabled={modalContent.data.isEnabled}
              title={modalContent.data.title}
            />
          )}
        </Modal>
      )}
    </ListCard>
  );
};

export default UsageCard;
